import {graphql, useFragment} from 'react-relay'

import type {
  useRecentAgentActivity_issue$data,
  useRecentAgentActivity_issue$key,
} from './__generated__/useRecentAgentActivity_issue.graphql'

export type AgentActionType = 'label_added' | 'type_set' | 'field_set'

export type AgentAction = {
  type: AgentActionType
  fieldName: string
  value: string | null
  createdAt: string
  color: string | null
}

export type AgentActivityGroup = {
  agentName: string
  agentSlug: string
  actorLogin: string
  actorAvatarUrl: string
  actions: AgentAction[]
}

export type ProcessedAgentActivity = {
  hasRecentActivity: boolean
  groups: AgentActivityGroup[]
  totalActionCount: number
}

export type RecentAgentActivityResult = ProcessedAgentActivity & {
  issueId: string
  viewerCanUpdateMetadata: boolean
}

export const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000

export function computeRecentActivitySince(): string {
  const hourMs = 60 * 60 * 1000
  const rounded = Math.floor((Date.now() - SEVEN_DAYS_MS) / hourMs) * hourMs
  return new Date(rounded).toISOString()
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const recentAgentActivityFragment = graphql`
  fragment useRecentAgentActivity_issue on Issue @argumentDefinitions(since: {type: "DateTime"}) {
    id
    viewerCanUpdateMetadata
    recentTriageEvents: timelineItems(
      last: 25
      since: $since
      visibleEventsOnly: true
      itemTypes: [
        LABELED_EVENT
        UNLABELED_EVENT
        ISSUE_TYPE_ADDED_EVENT
        ISSUE_TYPE_CHANGED_EVENT
        ISSUE_TYPE_REMOVED_EVENT
        ISSUE_FIELD_CHANGED_EVENT
        ISSUE_FIELD_ADDED_EVENT
        ISSUE_FIELD_REMOVED_EVENT
      ]
    ) {
      nodes {
        __typename
        ... on LabeledEvent {
          createdAt
          label {
            name
            color
          }
          actor {
            __typename
            login
            avatarUrl
            ... on Bot {
              isCopilot
              isAgent
              displayName
            }
          }
        }
        ... on UnlabeledEvent {
          createdAt
          label {
            name
            color
          }
          actor {
            __typename
            login
            avatarUrl
            ... on Bot {
              isCopilot
              isAgent
              displayName
            }
          }
        }
        ... on IssueTypeAddedEvent {
          createdAt
          issueType {
            name
          }
          actor {
            __typename
            login
            avatarUrl
            ... on Bot {
              isCopilot
              isAgent
              displayName
            }
          }
        }
        ... on IssueTypeChangedEvent {
          createdAt
          issueType {
            name
          }
          actor {
            __typename
            login
            avatarUrl
            ... on Bot {
              isCopilot
              isAgent
              displayName
            }
          }
        }
        ... on IssueTypeRemovedEvent {
          createdAt
          issueType {
            name
          }
          actor {
            __typename
            login
            avatarUrl
            ... on Bot {
              isCopilot
              isAgent
              displayName
            }
          }
        }
        ... on IssueFieldChangedEvent {
          createdAt
          issueField {
            ... on IssueFieldCommon {
              name
            }
          }
          newValue
          newColor
          actor {
            __typename
            login
            avatarUrl
            ... on Bot {
              isCopilot
              isAgent
              displayName
            }
          }
        }
        ... on IssueFieldAddedEvent {
          createdAt
          issueField {
            ... on IssueFieldCommon {
              name
            }
          }
          value
          color
          actor {
            __typename
            login
            avatarUrl
            ... on Bot {
              isCopilot
              isAgent
              displayName
            }
          }
        }
        ... on IssueFieldRemovedEvent {
          createdAt
          issueField {
            ... on IssueFieldCommon {
              name
            }
          }
          actor {
            __typename
            login
            avatarUrl
            ... on Bot {
              isCopilot
              isAgent
              displayName
            }
          }
        }
      }
    }
  }
`

type TimelineNode = NonNullable<NonNullable<useRecentAgentActivity_issue$data['recentTriageEvents']>['nodes']>[number]

type AgentInfo = {name: string; slug: string; login: string; avatarUrl: string}

function getAgentInfo(node: NonNullable<TimelineNode>): AgentInfo | null {
  if (!('actor' in node) || !node.actor) return null
  if (node.actor.__typename !== 'Bot') return null
  if (!node.actor.isCopilot && !node.actor.isAgent) return null
  return {
    name: node.actor.displayName ?? node.actor.login,
    slug: node.actor.login,
    login: node.actor.login,
    avatarUrl: node.actor.avatarUrl,
  }
}

function getFieldKey(node: NonNullable<TimelineNode>): string | null {
  switch (node.__typename) {
    case 'LabeledEvent':
    case 'UnlabeledEvent':
      return node.label?.name ? `label:${node.label.name}` : null
    case 'IssueTypeAddedEvent':
    case 'IssueTypeChangedEvent':
    case 'IssueTypeRemovedEvent':
      return 'type'
    case 'IssueFieldChangedEvent':
    case 'IssueFieldAddedEvent':
    case 'IssueFieldRemovedEvent':
      return node.issueField?.name ? `field:${node.issueField.name}` : null
    default:
      return null
  }
}

function toAgentAction(node: NonNullable<TimelineNode>): AgentAction | null {
  switch (node.__typename) {
    case 'LabeledEvent':
      return {
        type: 'label_added',
        fieldName: 'Label',
        value: node.label?.name ?? null,
        createdAt: node.createdAt,
        color: node.label?.color ?? null,
      }
    case 'IssueTypeAddedEvent':
    case 'IssueTypeChangedEvent':
      return {
        type: 'type_set',
        fieldName: 'Type',
        value: node.issueType?.name ?? null,
        createdAt: node.createdAt,
        color: null,
      }
    case 'IssueFieldChangedEvent':
      return {
        type: 'field_set',
        fieldName: node.issueField?.name ?? 'Field',
        value: node.newValue ?? null,
        createdAt: node.createdAt,
        color: node.newColor ?? null,
      }
    case 'IssueFieldAddedEvent':
      return {
        type: 'field_set',
        fieldName: node.issueField?.name ?? 'Field',
        value: node.value ?? null,
        createdAt: node.createdAt,
        color: node.color ?? null,
      }
    default:
      return null
  }
}

export function processAgentActivity(nodes: ReadonlyArray<TimelineNode | null>): ProcessedAgentActivity {
  const seenFields = new Set<string>()
  const groupsBySlug = new Map<string, AgentActivityGroup>()
  let totalActionCount = 0

  for (let i = nodes.length - 1; i >= 0; i--) {
    const node = nodes[i]
    if (!node || !('createdAt' in node)) continue

    const fieldKey = getFieldKey(node)
    if (!fieldKey || seenFields.has(fieldKey)) continue

    const agent = getAgentInfo(node)
    if (!agent) {
      seenFields.add(fieldKey)
      continue
    }

    const action = toAgentAction(node)
    if (!action) continue

    seenFields.add(fieldKey)

    let group = groupsBySlug.get(agent.slug)
    if (!group) {
      group = {
        agentName: agent.name,
        agentSlug: agent.slug,
        actorLogin: agent.login,
        actorAvatarUrl: agent.avatarUrl,
        actions: [],
      }
      groupsBySlug.set(agent.slug, group)
    }
    group.actions.unshift(action)
    totalActionCount++
  }

  const groups = Array.from(groupsBySlug.values())
  groups.sort((a, b) => a.agentSlug.localeCompare(b.agentSlug))

  return {
    hasRecentActivity: totalActionCount > 0,
    groups,
    totalActionCount,
  }
}

export function useRecentAgentActivity(issueRef: useRecentAgentActivity_issue$key): RecentAgentActivityResult {
  const data = useFragment(recentAgentActivityFragment, issueRef)
  const nodes = data.recentTriageEvents?.nodes ?? []
  const result = processAgentActivity(nodes)
  return {
    ...result,
    issueId: data.id,
    viewerCanUpdateMetadata: data.viewerCanUpdateMetadata ?? false,
  }
}
